import { FC, useEffect, useMemo } from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import useFormWarning from 'hooks/useFormWarning';
import { isEqual } from 'lodash';
import { Button } from 'ui/components/common';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { cn } from 'utils/helpers';

import { EVENT_FORM } from '../schema';
import AddDescription from './AddDescription';
import AddGuest from './AddGuest';
import AddLink from './AddLink';
import AddLocation from './AddLocation';
import AddTag from './AddTag';
import AddTitle from './AddTitle';
import Creator from './Creator';
import EventTime from './EventTime';
import EventType from './EventType';

interface PROPS {
  isLoading: boolean;
  isCreated?: boolean;
  onCancel?: () => void;
}

const EventForm: FC<PROPS> = ({ isLoading, isCreated, onCancel }) => {
  const { isOpen } = useTour();
  const {
    handleSubmit,
    errors,
    values,
    isSubmitting,
    setValues,
    initialValues,
  } = useFormikContext<EVENT_FORM>();

  const { onBack } = useFormWarning({
    isDirty: !isEqual(initialValues, values),
    item: 'event',
  });

  const handleClickNo = () => {
    onCancel?.();
    onBack();
  };

  const renderField = useMemo(() => {
    switch (values.type) {
      case 'VIDEO':
        return <AddLink />;
      case 'EVENT':
        return <AddLocation />;
      default:
        return <div />;
    }
  }, [values.type]);

  useEffect(() => {
    if (isOpen) {
      setValues({
        title: 'How to create an Event',
        guests: [
          { emailAddress: 'guest@user.com', firstName: '', lastName: '' },
        ],
        date: values.date,
        endAt: values.endAt,
        type: 'CALL',
        notifyBefore: values.notifyBefore,
        startAt: values.startAt,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validEvent = useMemo(() => {
    switch (values?.type) {
      case 'VIDEO':
        if (!values?.eventUrl) return false;
        return true;
      case 'EVENT':
        if (!values?.location) return false;
        return true;
      default:
        return true;
    }
  }, [values]);

  return (
    <form onSubmit={handleSubmit}>
      <div data-tut={TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.FORM.FORM}>
        <AddTitle />
        <EventType />
        {renderField}
        <EventTime isCreated={isCreated} />
        <AddGuest />
        <AddDescription />
        <Creator />
        <AddTag />
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={handleClickNo}
          label="NO"
          size="sm"
          intent="tertiary"
          className="font-semibold"
        />
        <Button
          type="submit"
          label="SAVE"
          size="sm"
          className="font-semibold"
          disabled={
            (isLoading ||
              isSubmitting ||
              !!Object.keys(errors).length ||
              !values.title ||
              !validEvent) &&
            !isOpen
          }
          data-tut={TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.FORM.SAVE_BUTTON}
        />
      </div>
    </form>
  );
};

export default EventForm;
